import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Grid,
  Pagination,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SchoolIcon from "@mui/icons-material/School";
import { useNavigate, useSearchParams } from "react-router-dom";
import { searchByPartnerPreferences } from "../../../api/api";
import { parseSearchParams } from "../../../utils/searchFilters";

const EMPTY_RESULT = {
  data: [],
  total: 0,
  page: 1,
  limit: 20,
  totalPages: 0,
  hasNextPage: false,
  hasPrevPage: false,
};

const ProfileSkeleton = () => (
  <Grid item xs={12} sm={6} md={4} lg={3}>
    <Card>
      <Skeleton variant="rectangular" height={210} />
      <CardContent>
        <Skeleton width="65%" />
        <Skeleton width="85%" />
        <Skeleton width="55%" />
      </CardContent>
    </Card>
  </Grid>
);

const FilteredCustomers = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryString = searchParams.toString();
  const filters = useMemo(() => parseSearchParams(queryString), [queryString]);
  const [result, setResult] = useState(EMPTY_RESULT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    let active = true;

    const fetchProfiles = async () => {
      setLoading(true);
      setError("");

      try {
        const response = await searchByPartnerPreferences(filters);
        if (active) {
          setResult({ ...EMPTY_RESULT, ...response.data });
        }
      } catch (requestError) {
        if (active) {
          setResult(EMPTY_RESULT);
          setError(
            requestError?.response?.data?.error?.message ||
              requestError?.message ||
              "Unable to search profiles. Please try again.",
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchProfiles();
    return () => {
      active = false;
    };
  }, [filters, requestVersion]);

  const handlePageChange = (_, nextPage) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("page", String(nextPage));
    setSearchParams(nextParams);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h5">Search results</Typography>
          {!loading && !error && (
            <Typography variant="body2" color="text.secondary">
              {result.total} {result.total === 1 ? "profile" : "profiles"} found
            </Typography>
          )}
        </Box>
        <Button
          variant="outlined"
          onClick={() => navigate(`/auth/search-by-partner?${queryString}`)}
        >
          Change filters
        </Button>
      </Stack>

      {error && (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              onClick={() => setRequestVersion((v) => v + 1)}
            >
              Retry
            </Button>
          }
          sx={{ mb: 3 }}
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {loading ? (
          Array.from({ length: 8 }, (_, index) => (
            <ProfileSkeleton key={index} />
          ))
        ) : result.data.length ? (
          result.data.map((profile) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={profile.id}>
              <Card sx={{ height: "100%" }}>
                <CardActionArea
                  onClick={() =>
                    navigate(`/auth/home/view-profile/${profile.id}`)
                  }
                  sx={{ height: "100%", alignItems: "stretch" }}
                >
                  <CardMedia
                    component="img"
                    height="210"
                    image={profile.image}
                    alt={profile.name || "Profile photo"}
                    sx={{ objectFit: "contain" }}
                  />
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} noWrap>
                      {profile.name || profile.profile_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={1}>
                      {[
                        profile.age && `${profile.age} yrs`,
                        profile.height && `${profile.height} cm`,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "Profile details not provided"}
                    </Typography>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <SchoolIcon fontSize="small" color="action" />
                      <Typography variant="body2" noWrap>
                        {profile.education || "Education not provided"}
                      </Typography>
                    </Stack>
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      mt={0.5}
                    >
                      <LocationOnIcon fontSize="small" color="action" />
                      <Typography variant="body2" noWrap>
                        {[profile.city, profile.country]
                          .filter(Boolean)
                          .join(", ") || "Location not provided"}
                      </Typography>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))
        ) : !error ? (
          <Grid item xs={12}>
            <Box textAlign="center" py={8}>
              <Typography variant="h6">No matching profiles found</Typography>
              <Typography color="text.secondary" mt={1} mb={3}>
                Try widening your age, height, or income range, or remove some
                filters.
              </Typography>
              <Button
                variant="contained"
                onClick={() =>
                  navigate(`/auth/search-by-partner?${queryString}`)
                }
              >
                Update search
              </Button>
            </Box>
          </Grid>
        ) : null}
      </Grid>

      {!loading && !error && result.totalPages > 1 && (
        <Stack alignItems="center" mt={5} mb={3}>
          <Pagination
            count={result.totalPages}
            page={result.page}
            color="primary"
            onChange={handlePageChange}
          />
        </Stack>
      )}
    </Box>
  );
};

export default FilteredCustomers;
